import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useGameStore } from "../../store/gameStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Dices, Sparkles } from "lucide-react";
import TriggerBadge from "./TriggerBadge";

// Mirrors ObstacleTriggerModal — the player-facing roll prompt for an auto-trigger Portal that
// has a DC/skill attached (trigger_mode "auto" with trigger_dc set). Pass teleports (the actual
// move already happened server-side by the time this resolves), fail just shows the Portal's
// configured fail effect and leaves them right where they are.
export default function DoorTriggerCheckModal({ gameId, character, myMemberId, isGm }) {
  const pending = useGameStore((s) => s.pendingDoorTriggerCheck);
  const setPending = useGameStore((s) => s.setPendingDoorTriggerCheck);
  const [stats, setStats] = useState(null);
  const [rolling, setRolling] = useState(false);
  const [result, setResult] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const isMine = pending && pending.member_id === myMemberId;

  useEffect(() => {
    setAcknowledged(false);
    if (isMine && character?.id) {
      api.get(`/characters/${character.id}/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
    }
  }, [pending?.door_id]);

  useEffect(() => {
    if (pending && isGm && !isMine) {
      toast.info(`${pending.token_name || "A player"} stepped onto: ${pending.label}`);
    }
  }, [pending?.door_id, pending?.member_id]);

  if (!isMine) return null;

  const close = () => {
    setPending(null);
    setResult(null);
    setStats(null);
    setAcknowledged(false);
  };

  const doRoll = async () => {
    if (!character?.id) {
      toast.error("Create a character first to roll");
      return;
    }
    setRolling(true);
    try {
      let total;
      const skillMod = pending.skill && stats?.skills?.[pending.skill]?.modifier;
      if (typeof skillMod === "number") {
        const { data } = await api.post(`/characters/${character.id}/roll`, { game_id: gameId, label: `${pending.label} (${pending.skill})`, modifier: skillMod });
        total = data.roll_breakdown.total;
      } else {
        const { data } = await api.post(`/characters/${character.id}/paper-roll`, { game_id: gameId, die: "1d20", bonuses: [], label: pending.label });
        total = data.roll_breakdown.total;
      }
      const passed = total >= pending.dc;
      const { data: resolveData } = await api.post(`/games/${gameId}/doors/${pending.door_id}/trigger-check`, { passed, token_id: pending.token_id });
      setResult({ total, pass: passed, effect: resolveData?.effect_result });
    } catch {
      toast.error("Roll failed");
    } finally {
      setRolling(false);
    }
  };

  return (
    <>
      {!acknowledged && (
        <TriggerBadge testId="door-trigger-check-badge" label={pending.label} onClick={() => setAcknowledged(true)} />
      )}
      <Dialog open={acknowledged} onOpenChange={(v) => !v && close()}>
        <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-sm" data-testid="door-trigger-check-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#D4AF37]" /> {pending.label}</DialogTitle>
            <DialogDescription>Roll to beat DC {pending.dc}{pending.skill ? ` (${pending.skill})` : ""}</DialogDescription>
          </DialogHeader>
          {result ? (
            <div className="space-y-3">
              <p className={`text-2xl font-display ${result.pass ? "text-[#4d7c0f]" : "text-[#D90429]"}`} data-testid="door-trigger-check-result">
                Rolled {result.total} — {result.pass ? "Through you go!" : "Nothing happens…"}
              </p>
              {result.effect?.message && (
                <p data-testid="door-trigger-check-effect-message" className="text-sm italic text-[#f1e0c5]/80 bg-black/30 border border-white/10 rounded-lg p-2">
                  {result.effect.message}
                </p>
              )}
              {result.effect?.damage && (
                <p data-testid="door-trigger-check-effect-damage" className="text-sm text-[#D90429]">
                  Took {result.effect.damage.final} {result.effect.damage.type || ""} damage
                  {result.effect.damage.hp_after != null ? ` — HP now ${result.effect.damage.hp_after}` : ""}
                </p>
              )}
              <Button data-testid="door-trigger-check-okay-button" onClick={close} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]">Okay</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {!character?.id && <p className="text-xs text-[#f1e0c5]/50">Create a character to roll for this check.</p>}
              <Button data-testid="door-trigger-check-roll-button" onClick={doRoll} disabled={rolling || !character?.id} className="w-full rounded-full gap-2 bg-[#D90429] hover:bg-[#b8031f]">
                <Dices className="w-4 h-4" /> Roll
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
